import { useSelector } from "react-redux";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";

const useTenant = () =>
  useSelector((state) => state.auth.user?.tenant) ||
  localStorage.getItem("kinesin-tenant");

// Optimistically zero the unreadCount for the given DM partner in the
// cached `chats` list so the badge clears the instant the user opens
// the conversation, before the server roundtrip resolves.
const optimisticZeroDmUnread = (queryClient, tenantVal, partnerStaffId) => {
  queryClient.setQueryData(["chats", tenantVal], (previous) => {
    if (!Array.isArray(previous)) return previous;

    return previous.map((chat) =>
      Number(chat?.staffId) === Number(partnerStaffId)
        ? { ...chat, unreadCount: 0, hasUnreadMessages: false }
        : chat
    );
  });
};

const optimisticZeroGroupUnread = (queryClient, tenantVal, groupId) => {
  queryClient.setQueryData(["chatGroups", tenantVal], (previous) => {
    if (!Array.isArray(previous)) return previous;

    return previous.map((group) =>
      Number(group?.id) === Number(groupId)
        ? { ...group, unreadCount: 0, hasUnreadMessages: false }
        : group
    );
  });
};

const optimisticZeroSummary = (queryClient, tenantVal, scope, amount) => {
  queryClient.setQueryData(["chatUnreadSummary", tenantVal], (previous) => {
    if (!previous) return previous;
    const dropDm = scope === "dm" ? Math.min(previous.dmUnread || 0, amount) : 0;
    const dropGroup =
      scope === "group" ? Math.min(previous.groupUnread || 0, amount) : 0;

    return {
      ...previous,
      dmUnread: (previous.dmUnread || 0) - dropDm,
      groupUnread: (previous.groupUnread || 0) - dropGroup,
      totalUnread: (previous.totalUnread || 0) - dropDm - dropGroup,
    };
  });
};

export const useMarkDmRead = () => {
  const queryClient = useQueryClient();
  const tenantVal = useTenant();

  return useMutation({
    mutationFn: async ({ partnerStaffId, upToChatId }) => {
      const axiosInstance = createAxiosInstance();
      const body = upToChatId ? { upToChatId } : {};
      const response = await axiosInstance.post(
        `${tenantVal}/chat/read/dm/${partnerStaffId}`,
        body
      );

      return response.data;
    },
    onMutate: ({ partnerStaffId }) => {
      const previousChats = queryClient.getQueryData(["chats", tenantVal]);
      const partnerEntry = Array.isArray(previousChats)
        ? previousChats.find(
            (chat) => Number(chat?.staffId) === Number(partnerStaffId)
          )
        : null;
      const previousUnread = partnerEntry?.unreadCount || 0;

      optimisticZeroDmUnread(queryClient, tenantVal, partnerStaffId);
      optimisticZeroSummary(queryClient, tenantVal, "dm", previousUnread);
    },
    onSettled: () => {
      // Mark-read is idempotent and returns 404 for unknown partners —
      // swallow errors here; the badge has already been zeroed.
      queryClient.invalidateQueries({ queryKey: ["chats", tenantVal] });
      queryClient.invalidateQueries({
        queryKey: ["chatUnreadSummary", tenantVal],
      });
    },
    // Silent on failure: a 404 (unknown partner) or 403 should not
    // surface a toast — the user did nothing wrong.
    onError: () => {},
  });
};

export const useMarkGroupRead = () => {
  const queryClient = useQueryClient();
  const tenantVal = useTenant();

  return useMutation({
    mutationFn: async ({ groupId, upToChatId }) => {
      const axiosInstance = createAxiosInstance();
      const body = upToChatId ? { upToChatId } : {};
      const response = await axiosInstance.post(
        `${tenantVal}/chat/read/group/${groupId}`,
        body
      );

      return response.data;
    },
    onMutate: ({ groupId }) => {
      const previousGroups = queryClient.getQueryData([
        "chatGroups",
        tenantVal,
      ]);
      const groupEntry = Array.isArray(previousGroups)
        ? previousGroups.find((group) => Number(group?.id) === Number(groupId))
        : null;
      const previousUnread = groupEntry?.unreadCount || 0;

      optimisticZeroGroupUnread(queryClient, tenantVal, groupId);
      optimisticZeroSummary(queryClient, tenantVal, "group", previousUnread);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["chatGroups", tenantVal] });
      queryClient.invalidateQueries({
        queryKey: ["chatUnreadSummary", tenantVal],
      });
    },
    onError: () => {},
  });
};
